/**
 * Integrations API client — Composio v3.
 *
 * Thin wrappers around the ``/api/v1/integrations/*`` endpoints
 * delivered in Phase 1. All success responses follow the
 * ``{"status":"success","data":...}`` envelope; this module unwraps
 * ``data`` so callers see clean payloads. Errors throw
 * :class:`IntegrationsApiError` with the parsed body and HTTP status.
 */

import { apiRequest, type ApiError } from "./client";
import { COMPOSIO_UI_ENABLED } from "@/lib/composio";
import { showPlanLimitToast } from "@/lib/upgrade-toast";
import { translate } from "@/i18n";
import { libErrorsMessages } from "@/i18n/messages/lib-errors";
import { libPlanMessages } from "@/i18n/messages/lib-plan";

/* ---------- Re-exports kept for back-compat with Phase 0 stubs ---------- */

/**
 * Master switch for the Composio UI surfaces. Kept here for the few
 * call sites (settings modal, master layout, settings page) that
 * imported it from ``@/api/integrations`` during Phase 0. New code
 * should import from ``@/lib/composio`` directly.
 */
export const INTEGRATIONS_UI_ENABLED = COMPOSIO_UI_ENABLED;

/* ---------- Types ---------- */

/** Lowercase status enum mirroring ``IntegrationConnection.Status``. */
export type ConnectionStatus =
  | "initiated"
  | "active"
  | "expired"
  | "failed"
  | "inactive";

export interface Toolkit {
  /** Composio slug (lowercase, e.g. ``"gmail"``). */
  name: string;
  display_name: string;
  description: string;
  icon_url: string;
  categories: string[];
}

/** One page of the Composio catalogue, returned by ``GET /apps/``. */
export interface ToolkitPage {
  items: Toolkit[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface IntegrationConnection {
  id: string;
  toolkit_slug: string;
  status: ConnectionStatus;
  connected_at: string | null;
  created_at: string;
}

export type ConnectionCredentialValue = string | number | boolean;

export interface ConnectionSetupField {
  name: string;
  label: string;
  type: "string" | "number" | "boolean";
  required: boolean;
  secret: boolean;
  description?: string;
  default?: ConnectionCredentialValue;
}

export interface ToolkitConnectionSetup {
  toolkit_slug: string;
  display_name: string;
  icon_url: string;
  auth_scheme: string;
  fields: ConnectionSetupField[];
  setup_url?: string;
  instructions?: string;
}

export interface InitiateConnectionResult {
  redirect_url: string;
  connection_id: string;
  status: ConnectionStatus;
}

export interface IntegrationTool {
  name: string;
  description?: string;
  toolkit_slug?: string;
  input_schema?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ExecuteToolResult {
  [key: string]: unknown;
}

/* ---------- Envelope helper ---------- */

interface ApiSuccess<T> {
  status: "success";
  data: T;
}

/**
 * Error thrown by every function in this module on non-2xx responses.
 * Wraps the underlying :class:`ApiError` (from ``client.ts``) with a
 * pre-extracted ``message`` for UI rendering.
 */
export class IntegrationsApiError extends Error {
  status: number;
  details: Record<string, unknown>;

  constructor(status: number, body: Record<string, unknown>) {
    const message =
      (typeof body.error === "string" && body.error) ||
      (typeof body.detail === "string" && body.detail) ||
      `Integrations request failed (HTTP ${status})`;
    super(message);
    this.name = "IntegrationsApiError";
    this.status = status;
    this.details = (body.details as Record<string, unknown>) ?? {};
  }
}

function toIntegrationsError(err: unknown): IntegrationsApiError {
  if (err instanceof IntegrationsApiError) return err;
  if (typeof err === "object" && err !== null && "status" in err) {
    const api = err as ApiError;
    if (api.status === 403 && api.data?.code === "plan_limit_exceeded") {
      showPlanLimitToast(
        typeof api.data.error === "string"
          ? api.data.error
          : translate(libPlanMessages, "appLimitReached"),
      );
    }
    return new IntegrationsApiError(api.status, api.data ?? {});
  }
  const msg =
    err instanceof Error
      ? err.message
      : translate(libErrorsMessages, "networkError");
  return new IntegrationsApiError(0, { error: msg });
}

async function call<T>(
  url: string,
  init?: { method?: string; body?: unknown },
): Promise<T> {
  try {
    const res = await apiRequest<ApiSuccess<T>>(url, {
      method: init?.method ?? "GET",
      body: init?.body,
      // Errors are surfaced via thrown IntegrationsApiError so the
      // store can decide what to do. Suppress the default toast.
      silent: true,
    });
    return res.data;
  } catch (err) {
    throw toIntegrationsError(err);
  }
}

/* ---------- Endpoints ---------- */

/** ``GET /apps/`` — one page of the Composio toolkit catalogue. */
export async function listToolkits(
  options: { search?: string; page?: number } = {},
): Promise<ToolkitPage> {
  const params = new URLSearchParams();
  if (options.search) params.set("search", options.search);
  if (options.page) params.set("page", String(options.page));
  const qs = params.toString();
  return call<ToolkitPage>(`/api/v1/integrations/apps/${qs ? `?${qs}` : ""}`);
}

/** ``GET /connections/`` — user's current connections. */
export async function listConnections(): Promise<IntegrationConnection[]> {
  return call<IntegrationConnection[]>("/api/v1/integrations/connections/");
}

export async function getToolkitConnectionSetup(
  toolkitSlug: string,
): Promise<ToolkitConnectionSetup> {
  return call<ToolkitConnectionSetup>(
    `/api/v1/integrations/apps/${encodeURIComponent(toolkitSlug)}/setup/`,
  );
}

/** Submit directly; credentials must never enter chat, frame content or stores. */
export async function connectWithCredentials(
  toolkitSlug: string,
  fields: Record<string, ConnectionCredentialValue>,
): Promise<IntegrationConnection> {
  return call<IntegrationConnection>(
    "/api/v1/integrations/connections/credentials/",
    { method: "POST", body: { toolkit_slug: toolkitSlug, fields } },
  );
}

/** ``POST /connections/`` — kick off an OAuth flow for a toolkit. */
export async function initiateConnection(
  toolkitSlug: string,
): Promise<InitiateConnectionResult> {
  return call<InitiateConnectionResult>("/api/v1/integrations/connections/", {
    method: "POST",
    body: { toolkit_slug: toolkitSlug },
  });
}

/** ``GET /connections/<toolkit>/status/`` — poll a single connection. */
export async function getConnectionStatus(
  toolkitSlug: string,
): Promise<IntegrationConnection> {
  return call<IntegrationConnection>(
    `/api/v1/integrations/connections/${encodeURIComponent(toolkitSlug)}/status/`,
  );
}

/** ``DELETE /connections/<toolkit>/`` — disconnect a toolkit. */
export async function disconnectToolkit(toolkitSlug: string): Promise<void> {
  try {
    await apiRequest<void>(
      `/api/v1/integrations/connections/${encodeURIComponent(toolkitSlug)}/`,
      { method: "DELETE", silent: true },
    );
  } catch (err) {
    throw toIntegrationsError(err);
  }
}

/** ``GET /tools/?toolkits=csv`` — JSON-Schema tool defs (cached server-side). */
export async function listTools(
  toolkitSlugs?: string[],
): Promise<IntegrationTool[]> {
  const qs =
    toolkitSlugs && toolkitSlugs.length > 0
      ? `?toolkits=${encodeURIComponent(toolkitSlugs.join(","))}`
      : "";
  return call<IntegrationTool[]>(`/api/v1/integrations/tools/${qs}`);
}

/** ``POST /tools/execute/`` — run a Composio tool. */
export async function executeTool(args: {
  tool_name: string;
  arguments: Record<string, unknown>;
  conversation_id?: string;
}): Promise<ExecuteToolResult> {
  return call<ExecuteToolResult>("/api/v1/integrations/tools/execute/", {
    method: "POST",
    body: args,
  });
}
